import React, { startTransition, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useBundleTranslation from 'i18n';
import {
    AccessDeniedTileInfo,
    buildFolderMap,
    decompressTiles,
    getGroupByOptions,
    getSegmentOptions,
    HomeContextTypeCustomEvents,
    HomeContextTypeVisibility,
    HomePageData,
    HomePageState,
    TileInfo,
    TopicsPopupProps,
} from 'app/home/index.functions';
import { homePageStateDefault, Tile4FavoriteData } from 'app/home/index';
import { HomeContext } from 'app/home/context';
import { cloneDeep } from 'lodash';
import { homePage } from 'api/home-page';
import { useQuery } from '@tanstack/react-query';
import md5 from 'crypto-js/md5';
import GlossaryTermPopup from 'components/glossary-term-popup';
import { TileSection } from 'app/home/tile/TilePanel.functions';
import { useCatalogIdByAlias } from 'hooks/useCatalog';
let isDataLoaded = false;

export interface IEmbedWrapperDataParams {
    [key: string]: any;
    visibility?: HomeContextTypeVisibility;
    custom_events?: HomeContextTypeCustomEvents;
}

export default function EmbedWrapper({
    dataParams,
    filters,
    children,
    isEmbedded = true,
}: {
    dataParams: IEmbedWrapperDataParams;
    filters?: any;
    children: any;
    isEmbedded?: boolean;
}) {
    const { t } = useBundleTranslation(['app/home']);
    const navigationBack = undefined !== dataParams['navigation_back'] && 'Y' == dataParams['navigation_back'];
    const isFavoriteFilter =
        undefined !== dataParams['favorite'] && dataParams['favorite'] > '' && dataParams['favorite'] > '0';
    const newTab = undefined !== dataParams['new_tab'] && 'Y' == dataParams['new_tab'];
    const catalog = undefined !== dataParams['catalog'] && dataParams['catalog'] > '' ? dataParams['catalog'] : '';
    const queryKeyPrefix = catalog > '' ? catalog + 'Catalog' : 'homePage';
    const currentCatalogId = useCatalogIdByAlias(catalog);

    const {
        data: homePageSettingsData,
        refetch: refetchHomeSettings,
        isLoading: isLoadingHomeSettings,
    } = useQuery<any | void, Error>({
        queryKey: [queryKeyPrefix + 'Settings' + md5(JSON.stringify(dataParams))],
        queryFn: () => homePage.getHomeSettings({ catalog }),
    });

    const {
        data: loadedData,
        isLoading,
        refetch,
    } = useQuery<any, Error>({
        queryKey: [queryKeyPrefix + 'Data_' + md5(JSON.stringify(dataParams))],
        queryFn: () => {
            return homePage.getData(dataParams).then((data) => {
                isDataLoaded = false;
                return data;
            });
        },
        refetchOnMount: true,
    });

    const [showTermsPopup, setShowTermsPopup] = useState(false);
    let trashBucketRef = React.useRef<HTMLDivElement>(null);
    const [tilePreview, setTilePreview] = useState<TileInfo | null>(null);
    const [accessDeniedPopupTile, setAccessDeniedPopupTile] = useState<AccessDeniedTileInfo | null>(null);
    const [visibleTiles, setVisibleTiles] = useState<TileInfo[]>([]);
    const tilesSectionsRef = useRef<TileSection[]>([]);
    const [topicId, setTopicId] = useState<number>(0);
    const [elementId, setElementId] = useState<number>(0);
    const [tile4Favorite, setTile4Favorite] = useState<Tile4FavoriteData | null>(null);

    const [data, setData] = useState<HomePageData>({
        data: { vocabulary: [], rows: [] },
        hierarchyFolders: [],
        folderItems: [],
        tags: [],
        tileIcons: {},
        contentTypes: [],
        customFieldsDisplayOnTileInfo: {},
    });
    const [homePageState, setHomePageState] = useState<HomePageState>({
        ...homePageStateDefault,
        filterState: {
            ...homePageStateDefault.filterState,
            groupBy: isFavoriteFilter ? '' : 'None',
            view: dataParams['list_view'] == 'Y' ? 'list' : 'tile',
            folderSelected: filters?.folderSelected ? filters.folderSelected : '',
        },
    });

    useEffect(() => {
        setHomePageState((prevState: HomePageState) => {
            const newState: any = prevState ? { ...prevState } : {};
            if (!newState?.filterState) newState.filterState = {};
            if (filters?.folderSelected) {
                newState.filterState.folderSelected = filters.folderSelected;
            } else {
                newState.filterState.folderSelected = '';
            }
            return newState;
        });
    }, [filters?.folderSelected]);

    const topicPopupProps: TopicsPopupProps = useMemo(() => {
        return {
            setShowTermsPopup,
            setTopicId,
            setElementId,
        };
    }, []);

    const tiles: TileInfo[] = useMemo(() => decompressTiles(data), [data]);

    const topicIds: number[] = useMemo(() => {
        const result: number[] = [];

        if (tiles && elementId > 0)
            tiles.forEach((tile) => {
                if (tile.topics && tile.element_id === elementId) result.push(...tile.topics);
            });

        return result;
    }, [tiles, elementId]);

    useEffect(() => {
        if (!showTermsPopup) {
            setElementId(0);
        }
    }, [showTermsPopup]);

    const [favorites, folders, categories, folderMap] = useMemo(
        () => buildFolderMap(cloneDeep(data.hierarchyFolders)),
        [data.hierarchyFolders],
    );

    const refetchData = useCallback(() => {
        refetch();
        isDataLoaded = false;
    }, [refetch]);

    useEffect(() => {
        if (!isLoading && !isDataLoaded) {
            isDataLoaded = true;
            startTransition(() => {
                setData({
                    data: loadedData.data,
                    hierarchyFolders: loadedData.hierarchyFolders,
                    folderItems: loadedData.folderItems,
                    tags: loadedData.topics,
                    tileIcons: loadedData.tileIcons,
                    contentTypes: loadedData.contentTypes,
                    customFieldsDisplayOnTileInfo: loadedData.customFieldsDisplayOnTileInfo,
                });
            });
        }
    }, [loadedData]);

    const handleContextMenu = (event: React.MouseEvent, tile: TileInfo | null) => {};

    const groupByOptions = getGroupByOptions(homePageSettingsData?.settings, t);
    const segmentOptions = getSegmentOptions(data, homePageSettingsData?.settings, tiles, '', t);
    const tagOptions = data?.tags.map((t) => ({ value: String(t.topic_id), label: t.full_name }));

    if (null == data || null == data.hierarchyFolders || 0 == data.hierarchyFolders.length) return <></>;

    return (
        <>
            <HomeContext.Provider
                value={{
                    tiles,
                    favorites,
                    folders,
                    categories,
                    folderMap,
                    homePageState,
                    setHomePageState,
                    data,
                    setData,
                    refetchData,
                    trashBucketRef,
                    tilePreview,
                    setTilePreview,
                    setAccessDeniedPopupTile,
                    setVisibleTiles,
                    tilesSectionsRef,
                    handleContextMenu,
                    setTile4Favorite,
                    groupByOptions,
                    segmentOptions,
                    tagOptions,
                    isEmbedded: isEmbedded,
                    navigationBack,
                    newTab,
                    topicPopupProps,
                    logoImageUrl: homePageSettingsData?.logoImageUrl,
                    homePageSettings: homePageSettingsData?.settings,
                    visibility: dataParams?.visibility,
                    customEvents: dataParams?.custom_events,
                    catalog,
                    currentCatalogId,
                }}
            >
                {children}
                <Suspense>
                    {showTermsPopup && (
                        <GlossaryTermPopup
                            setShowPopup={setShowTermsPopup}
                            showPopup={showTermsPopup}
                            topicIds={topicIds}
                            topicId={topicId}
                        />
                    )}
                </Suspense>
            </HomeContext.Provider>
        </>
    );
}
